import { useRef } from 'react';

import { Box, HStack, Skeleton, Text, Tooltip } from '@chakra-ui/react';

import { ACCOMMODATION_TILE } from '@app/consts';
import { amenityBoxStyles } from '@app/helpers/accommodation-tile-amenities.helpers';
import { getAmenityIcon } from '@app/helpers/get-amenity-icon';
import { useAmenitiesVisibility } from '@app/hooks/useAmenitiesVisibility';

import { AccommodationTileAmenitiesDynamicProps } from './accommodation-tile-amenities-dynamic.props';

export const AccommodationTileAmenitiesDynamic = ({
    sortedAmenities,
    getAmenityLabel,
    ...rest
}: AccommodationTileAmenitiesDynamicProps) => {
    const listRef = useRef<HTMLDivElement>(null);
    const amenityItemsRef = useRef<(HTMLDivElement | null)[]>([]);
    const overflowRef = useRef<HTMLDivElement>(null);

    const { isInitialized, hiddenLabels, overflowText } = useAmenitiesVisibility({
        listRef,
        amenityItemsRef,
        overflowRef,
        amenitiesCount: sortedAmenities.length,
    });

    // Reset before render so stale refs don't linger if the amenities list shrinks
    amenityItemsRef.current = [];

    return (
        <>
            <HStack
                ref={listRef}
                as={'ul'}
                gap={2}
                position={!isInitialized ? { md: 'absolute' } : undefined}
                visibility={!isInitialized ? { md: 'hidden' } : undefined}
                {...rest}
            >
                {sortedAmenities.map((amenity, index) => {
                    const icon = getAmenityIcon(amenity.type, {
                        fontSize: 'md',
                        css: { '--icon-stroke': 1 },
                    });
                    const label = getAmenityLabel(amenity);
                    const isBeyondLimit = index >= ACCOMMODATION_TILE.MAX_AMENITIES;

                    return (
                        <HStack
                            ref={(el: HTMLDivElement | null) => {
                                amenityItemsRef.current[index] = el;
                            }}
                            as={'li'}
                            key={`amenity-${amenity.type}-${index}`}
                            {...amenityBoxStyles}
                            px={{ base: 0, md: amenityBoxStyles.pl }}
                            w={{ base: 6, md: 'auto' }}
                            justify={{ base: 'center', md: 'flex-start' }}
                            display={{ base: isBeyondLimit ? 'none' : 'flex', md: 'flex' }}
                            data-testid={`acc-tile-amenity-${amenity.type}`}
                            data-amenity-label={label}
                        >
                            {icon}
                            <Text
                                as={'span'}
                                fontSize={'xs'}
                                whiteSpace={'nowrap'}
                                display={{ base: 'none', md: 'block' }}
                            >
                                {label}
                            </Text>
                        </HStack>
                    );
                })}

                <Tooltip.Root openDelay={0} closeDelay={0} positioning={{ placement: 'top' }}>
                    <Tooltip.Trigger asChild>
                        <Box
                            ref={overflowRef}
                            as={'li'}
                            hideBelow={'md'}
                            position={'relative'}
                            zIndex={'overLinkOverlay'}
                            onClick={e => e.stopPropagation()}
                        >
                            <HStack
                                as={'span'}
                                fontSize={'xs'}
                                {...amenityBoxStyles}
                                px={2}
                                whiteSpace={'nowrap'}
                                cursor={'default'}
                            >
                                {overflowText}
                            </HStack>
                        </Box>
                    </Tooltip.Trigger>
                    <Tooltip.Positioner>
                        <Tooltip.Content>
                            <Tooltip.Arrow>
                                <Tooltip.ArrowTip />
                            </Tooltip.Arrow>
                            {hiddenLabels.map((label, i) => (
                                <Box key={i} py={0.5} fontSize={'md'}>
                                    {label}
                                </Box>
                            ))}
                        </Tooltip.Content>
                    </Tooltip.Positioner>
                </Tooltip.Root>
            </HStack>

            {/* Skeleton shown on desktop only — mobile has no dynamic calculation */}
            {!isInitialized && (
                <HStack hideBelow={'md'} as={'ul'} gap={2} {...rest}>
                    <Skeleton as={'li'} h={6} w={14} borderRadius={'sm'} />
                    <Skeleton as={'li'} h={6} w={20} borderRadius={'sm'} />
                    <Skeleton as={'li'} h={6} w={16} borderRadius={'sm'} />
                </HStack>
            )}
        </>
    );
};
